import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface VideoGuide {
  id: string;
  title: string;
  description: string | null;
  vimeo_url: string;
  vimeo_id: string;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
}

export function parseVimeoId(url: string): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  const patterns = [
    /vimeo\.com\/(?:channels\/[^/]+\/|groups\/[^/]+\/videos\/|album\/\d+\/video\/)?(\d+)/i,
    /player\.vimeo\.com\/video\/(\d+)/i,
  ];
  for (const re of patterns) {
    const m = trimmed.match(re);
    if (m && m[1]) return m[1];
  }
  if (/^\d+$/.test(trimmed)) return trimmed;
  return null;
}

export const useVideoGuides = () => {
  return useQuery({
    queryKey: ["video-guides"],
    queryFn: async (): Promise<VideoGuide[]> => {
      const { data, error } = await supabase
        .from("video_guides")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as VideoGuide[];
    },
  });
};

export const useAddVideoGuide = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      title,
      description,
      vimeoUrl,
    }: {
      title: string;
      description?: string;
      vimeoUrl: string;
    }) => {
      const vimeoId = parseVimeoId(vimeoUrl);
      if (!vimeoId) throw new Error("Invalid Vimeo URL");

      const { data: userData } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from("video_guides")
        .insert({
          title,
          description: description || null,
          vimeo_url: vimeoUrl,
          vimeo_id: vimeoId,
          uploaded_by: userData.user?.id ?? null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["video-guides"] });
      toast({ title: "Video added", description: "Your video is now visible in the Help section." });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to add video", description: error.message, variant: "destructive" });
    },
  });
};

export const useDeleteVideoGuide = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("video_guides").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["video-guides"] });
      toast({ title: "Video deleted" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to delete video", description: error.message, variant: "destructive" });
    },
  });
};